import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, CheckCircle2, Truck, Home } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STEPS = [["pending", "Order Placed", Package], ["processing", "Processing", CheckCircle2], ["shipped", "Shipped", Truck], ["delivered", "Delivered", Home]];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("t") || "");
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  const track = async (c) => {
    setErr(""); setOrder(null);
    try { const { data } = await api.get(`/orders/track/${c}`); setOrder(data); }
    catch { setErr("No order found with that tracking number."); }
  };
  useEffect(() => { if (params.get("t")) track(params.get("t")); }, []);

  const statusIndex = order ? Math.max(0, STEPS.findIndex((s) => s[0] === order.status)) : 0;

  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] text-center">Order Status</p>
      <h1 className="font-display text-4xl text-center mt-2">Track Your Order</h1>
      <form onSubmit={(e) => { e.preventDefault(); code && track(code); }} className="flex gap-3 mt-8">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter tracking number (e.g. GLT...)" className="rounded-none h-12" data-testid="track-input" />
        <Button className="rounded-none h-12 px-8 bg-[#D4AF37] text-black font-semibold" data-testid="track-submit">Track</Button>
      </form>
      {err && <p className="text-destructive text-center mt-6" data-testid="track-error">{err}</p>}
      {order && (
        <div className="mt-12" data-testid="track-result">
          <div className="flex justify-between items-center mb-8">
            <div><p className="text-sm text-muted-foreground">Tracking</p><p className="font-display text-xl">{order.tracking_number}</p></div>
            <div className="text-right"><p className="text-sm text-muted-foreground">Total</p><p className="font-display text-xl">{formatPrice(order.total)}</p></div>
          </div>
          <div className="flex justify-between relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-border" />
            <div className="absolute top-5 left-0 h-0.5 bg-[#D4AF37] transition-all" style={{ width: `${(statusIndex / (STEPS.length - 1)) * 100}%` }} />
            {STEPS.map(([key, label, Icon], i) => (
              <div key={key} className="relative z-10 flex flex-col items-center gap-2 bg-background px-1">
                <div className={`grid h-10 w-10 place-items-center rounded-full ${i <= statusIndex ? "bg-[#D4AF37] text-black" : "bg-secondary text-muted-foreground"}`}><Icon className="h-5 w-5" /></div>
                <span className="text-xs text-center">{label}</span>
              </div>
            ))}
          </div>
          <div className="bg-[#F5F1E8] dark:bg-[#141414] p-6 mt-10 space-y-3">
            {order.items?.map((it, i) => <div key={i} className="flex justify-between text-sm"><span>{it.name} × {it.quantity}</span><span>{formatPrice(it.price * it.quantity)}</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
